// /staff — Create staff accounts.
// Spec: docs/plans/admin-tooling.md §4.4 + D-62.
//
// Authorization: existing authenticated staff only (no public signup).
// Step-up re-auth required before POST, matching the pattern used by the
// 9 existing mutating admin actions (user-profile.tsx + action-modal.tsx).

import { useState } from 'react';
import { apiFetch } from '@/lib/api';
import { StepUpModal } from '@/components/admin/step-up-modal';
import { Button, Panel, PanelHeader, PageHeader } from '@awakenedlands/ui';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CreateStaffResponse {
  ok: boolean;
  login_name: string;
  staff_id: number;
  totp_uri: string;
  totp_secret_b32: string;
  backup_codes: string[];
}

// ── Phase machine ─────────────────────────────────────────────────────────────

type Phase = 'stepup' | 'form' | 'submitting' | 'success' | 'error';

// ── Component ─────────────────────────────────────────────────────────────────

export function StaffPage() {
  const [phase, setPhase] = useState<Phase>('stepup');
  const [stepupToken, setStepupToken] = useState('');

  // Form fields
  const [loginName, setLoginName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Results
  const [enrollResult, setEnrollResult] = useState<CreateStaffResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  // ── Step-up verified ────────────────────────────────────────────────────────

  const handleVerified = (token: string) => {
    setStepupToken(token);
    setPhase('form');
  };

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    if (!loginName.trim() || !username.trim() || !email.trim() || password.length < 12) return;

    setPhase('submitting');

    void (async () => {
      const res = await apiFetch<CreateStaffResponse>('/admin/staff', {
        method: 'POST',
        body: JSON.stringify({
          login_name: loginName.trim().toLowerCase(),
          username: username.trim(),
          email: email.trim().toLowerCase(),
          password,
        }),
        headers: { 'X-Stepup-Token': stepupToken },
      });

      if (!res.ok) {
        const data = res.data as unknown as Record<string, unknown>;
        setErrorMsg((data['error'] as string | undefined) ?? 'Failed to create staff account.');
        setPhase('error');
        return;
      }

      setEnrollResult(res.data);
      setPhase('success');
    })();
  };

  const handleReset = () => {
    setLoginName('');
    setUsername('');
    setEmail('');
    setPassword('');
    setEnrollResult(null);
    setErrorMsg('');
    setPhase('stepup');
  };

  // ── Render: step-up ─────────────────────────────────────────────────────────

  if (phase === 'stepup') {
    return (
      <div className="max-w-xl">
        <PageHeader title="Staff Accounts" description="Re-authenticate to create a new staff account." />
        <StepUpModal
          actionIntent="create-staff"
          targetUserId={null}
          onVerified={handleVerified}
          onCancel={() => { /* stays on page; re-auth required */ }}
        />
      </div>
    );
  }

  // ── Render: success (one-time enrollment display) ───────────────────────────

  if (phase === 'success' && enrollResult) {
    return (
      <div className="max-w-xl space-y-6">
        <PageHeader
          title="Staff Account Created"
          description="Share the following with the new operator. These values are shown once and cannot be retrieved again."
        />

        <Panel>
          <PanelHeader>Account Details</PanelHeader>
          <div className="p-4 space-y-2 text-sm text-ink-mid">
            <p><span className="text-ink-low font-mono">login_name:</span> <span className="text-ink-high font-mono">{enrollResult.login_name}</span></p>
            <p><span className="text-ink-low font-mono">staff_id:</span> <span className="text-ink-high font-mono">{enrollResult.staff_id}</span></p>
          </div>
        </Panel>

        <Panel>
          <PanelHeader>TOTP Enrollment</PanelHeader>
          <div className="p-4 space-y-3 text-sm">
            <p className="text-ink-mid">Scan with Bitwarden, Google Authenticator, or Aegis:</p>
            <pre
              className="break-all whitespace-pre-wrap rounded-sm bg-panel px-3 py-2 text-xs font-mono text-ink-high border border-border"
              data-testid="totp-uri"
            >
              {enrollResult.totp_uri}
            </pre>
            <p className="text-ink-mid">Base32 secret (manual entry if QR fails):</p>
            <pre
              className="break-all whitespace-pre-wrap rounded-sm bg-panel px-3 py-2 text-xs font-mono text-ink-high border border-border"
              data-testid="totp-secret-b32"
            >
              {enrollResult.totp_secret_b32}
            </pre>
          </div>
        </Panel>

        <Panel>
          <PanelHeader>Backup Codes (8, single-use)</PanelHeader>
          <div className="p-4 space-y-2" data-testid="backup-codes">
            <p className="text-sm text-ink-mid mb-3">
              Store in a paper safe or Bitwarden. Each code can only be used once.
            </p>
            <ol className="space-y-1 list-decimal list-inside">
              {enrollResult.backup_codes.map((code, i) => (
                <li key={i} className="font-mono text-sm text-ink-high tracking-wider">{code}</li>
              ))}
            </ol>
          </div>
        </Panel>

        <div className="rounded-sm border border-[--color-status-warn] bg-[--color-status-warn]/10 px-4 py-3">
          <p className="text-sm font-semibold text-[--color-status-warn]">
            This is the only time these codes are shown. Close this page only after the new operator has saved them.
          </p>
        </div>

        <Button variant="secondary" onClick={handleReset}>Create another account</Button>
      </div>
    );
  }

  // ── Render: error ───────────────────────────────────────────────────────────

  if (phase === 'error') {
    return (
      <div className="max-w-xl space-y-6">
        <PageHeader title="Staff Accounts" />
        <Panel>
          <div className="p-4 space-y-4">
            <p role="alert" className="text-sm text-destructive-strong">{errorMsg}</p>
            <div className="flex gap-3">
              <Button onClick={() => setPhase('form')}>Back to Form</Button>
              <Button variant="secondary" onClick={handleReset}>Start Over</Button>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  // ── Render: form (phase==='form' or 'submitting') ───────────────────────────

  const isSubmitting = phase === 'submitting';
  const canSubmit = loginName.trim().length >= 3 &&
    username.trim().length >= 1 &&
    email.trim().includes('@') &&
    password.length >= 12;

  return (
    <div className="max-w-xl space-y-6">
      <PageHeader title="Staff Accounts" description="Create a new staff account. Fields are required." />

      <Panel>
        <PanelHeader>New Staff Member</PanelHeader>
        <div className="p-4 space-y-4">

          <div className="space-y-1">
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="login-name">
              Login Name <span className="text-ink-low">(3–20 chars, a-z0-9)</span>
            </label>
            <input
              id="login-name"
              type="text"
              value={loginName}
              onChange={(e) => setLoginName(e.target.value.toLowerCase())}
              maxLength={20}
              pattern="[a-z0-9]{3,20}"
              disabled={isSubmitting}
              className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              placeholder="newstaff"
              data-testid="field-login-name"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="username">
              Display Name
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              maxLength={64}
              disabled={isSubmitting}
              className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              placeholder="New Staff"
              data-testid="field-username"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              placeholder="staff@awakenedlands.com"
              data-testid="field-email"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-mid" htmlFor="password">
              Initial Password <span className="text-ink-low">(min 12 chars)</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              disabled={isSubmitting}
              className="w-full rounded-sm border border-border bg-panel px-3 py-2 text-sm text-ink-high focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              placeholder="••••••••••••"
              data-testid="field-password"
            />
            <p className="text-xs text-ink-low">
              Share this with the new operator via a secure channel. They can change it after login.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !canSubmit}
              className="flex-1"
              data-testid="submit-button"
            >
              {isSubmitting ? 'Creating…' : 'Create Staff Account'}
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
